import React, { useEffect, useMemo, useState } from "react";
import "./LoginModalv2Popup.scss";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { useTranslation, withTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { actionCreators } from "../../store";
import { RootState } from "../../store/reducers";
import InputMaskField from "../react-input-mask/inputfield";
import { apiService } from "../../services";
import {
  Countries,
  getCountryByCode,
  getMaxDigits,
  getMaskPattern,
  validatePhoneNumber,
  phonePatterns,
} from "../../utils/phoneValidation";

const LoginModalv2Popup = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    setLoginModal,
    setOtpModal,
    setLoginState,
    setPendingCheckout,
    freezScreenState,
    alertMessageState,
    langValueState,
  } = bindActionCreators(actionCreators, dispatch);

  const appState = useSelector((state: RootState) => state.appReducer);
  const apiState = useSelector((state: RootState) => state.apiReducer);
  const { loginModal, loginValues, langValue } = appState;
  const storeInfo = apiState.storeInfo;

  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null);
  const [form, setForm] = useState({
    phoneCode: loginValues?.phoneCode || "965",
    phoneNumber: loginValues?.phoneNumber || "",
    fullName: loginValues?.fullName || "",
    email: loginValues?.email || "",
    provider: storeInfo?.otpProvider || "",
    storeId: storeInfo?.storeId || "",
    branchId: storeInfo?.branchId || "",
  });

  const selectedCountry = useMemo(
    () =>
      Countries.find((c) => c.dial_code === `+${form.phoneCode}`) ||
      getCountryByCode(storeInfo?.countryNameCode) ||
      Countries[0],
    [form.phoneCode, storeInfo?.countryNameCode]
  );

  const countryList = useMemo(() => {
    const defaultCode = storeInfo?.countryNameCode || "KW";
    return [...Countries].sort((a, b) => {
      if (a.code === defaultCode) return -1;
      if (b.code === defaultCode) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [storeInfo?.countryNameCode]);

  useEffect(() => {
    if (loginModal) {
      setForm({
        phoneCode: loginValues?.phoneCode || selectedCountry.dial_code.replace("+", ""),
        phoneNumber: loginValues?.phoneNumber || "",
        fullName: loginValues?.fullName || "",
        email: loginValues?.email || "",
        provider: storeInfo?.otpProvider || "",
        storeId: storeInfo?.storeId || "",
        branchId: storeInfo?.branchId || "",
      });
    }
  }, [loginModal]);

  if (!loginModal) return null;

  const isRtl = langValue === "ar";
  const otpRequired = !!storeInfo?.otp;
  const phoneValid = validatePhoneNumber(form.phoneNumber, selectedCountry.code).isValid;
  const nameValid = !!form.fullName?.trim();
  const phonePattern = phonePatterns[selectedCountry.code];
  const phoneErrorText = phoneError
    ? t(`Login.${phoneError}`, { min: phonePattern?.min, max: phonePattern?.max })
    : "";
  const phoneHelperText = phoneValid
    ? t("Login.phone-valid")
    : phoneErrorText;
  const phoneHasError = !phoneValid && !!phoneError;

  const syncForm = (next: typeof form) => {
    setForm(next);
    setLoginState(next);
  };

  const updateField = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "phoneNumber") {
      const digits = value.replace(/\D/g, "");
      const maxDigits = getMaxDigits(selectedCountry.code);
      if (digits.length > maxDigits) return;
      const next = { ...form, phoneNumber: digits };
      syncForm(next);
      setPhoneError(
        digits.length ? validatePhoneNumber(digits, selectedCountry.code).error : ""
      );
      return;
    }
    syncForm({ ...form, [name]: value });
  };

  const handleCountryChange = (countryCode: string) => {
    const country = Countries.find((c) => c.code === countryCode) || Countries[0];
    syncForm({
      ...form,
      phoneCode: country.dial_code.replace("+", ""),
      phoneNumber: "",
    });
    setPhoneError("");
    setCountryMenuAnchor(null);
  };

  const toggleLang = () => {
    const next = isRtl ? "en" : "ar";
    langValueState(next);
    i18n.changeLanguage(next);
  };

  const handleClose = () => {
    setLoginModal(false);
    setPendingCheckout(false);
  };

  const phoneLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!phoneValid || !nameValid) {
      alertMessageState({
        type: "error",
        show: true,
        message: phoneErrorText || t("Login.plz-fill-required-fields"),
      });
      return;
    }

    setLoginState(form);

    if (!otpRequired) {
      setLoginModal(false);
      return;
    }

    setLoading(true);
    freezScreenState(true);
    try {
      const response: any = await apiService.guestOtp(form.phoneCode, form.phoneNumber, {
        store_id: storeInfo?.storeId || form.storeId || null,
        branch_id: storeInfo?.branchId || form.branchId || null,
        provider: storeInfo?.otpProvider || form.provider || null,
      });

      if (response?.data?.data === true || response?.data?.success === true) {
        alertMessageState({
          type: "success",
          show: true,
          message: response.data.message || t("Login.OTP-sent"),
        });
        setLoginModal(false);
        setOtpModal(true);
      } else {
        alertMessageState({
          type: "error",
          show: true,
          message: response?.data?.message || t("Login.Failed-to-send-OTP"),
        });
      }
    } catch (e: any) {
      alertMessageState({
        type: "error",
        show: true,
        message: e?.message || t("Login.check-network"),
      });
    } finally {
      setLoading(false);
      freezScreenState(false);
    }
  };

  return (
    <Modal
      open
      onClose={handleClose}
      disableAutoFocus={false}
      disableEnforceFocus
      disablePortal={false}
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.45)",
        },
      }}
      sx={{ zIndex: 140000 }}
    >
      <div
        className={`login-modal-sheet ${isRtl ? "login-modal-sheet--rtl direction-rtl" : "direction-ltr"}`}
        dir={isRtl ? "rtl" : "ltr"}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {loading && (
          <Backdrop
            open
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              borderRadius: "20px 20px 0 0",
              color: "#fff",
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

        <div className="login-modal-sheet__header">
          <button type="button" className="login-modal-sheet__back" onClick={handleClose} aria-label={t("Login.Back")}>
            {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
          </button>
          <button type="button" className="login-modal-sheet__lang" onClick={toggleLang}>
            <span className="login-modal-sheet__lang-code">{isRtl ? "EN" : "ع"}</span>
            <span className="login-modal-sheet__lang-label">{isRtl ? "English" : "العربية"}</span>
          </button>
        </div>

        <h3 className="login-modal-sheet__title familysora600">
          {t("Login.Verify-Your-Mobile-Number")}
        </h3>
        <p className="login-modal-sheet__subtitle familysora400">
          {otpRequired
            ? t("Login.You-will-recieve-a-one-time-password-shortly")
            : t("Login.enter-name-and-mobile")}
        </p>

        <form className="login-modal-sheet__form" onSubmit={phoneLogin}>
          <div className="login-modal-sheet__field" dir="ltr">
            <InputMaskField
              value={form.phoneNumber}
              onChange={updateField}
              inputProps={{ inputMode: "numeric", autoComplete: "off" }}
              label={t("Login.Phone-number")}
              variant="outlined"
              type="tel"
              mask={getMaskPattern(selectedCountry.code)}
              name="phoneNumber"
              error={phoneHasError}
              helperText={phoneHelperText}
              FormHelperTextProps={{
                className: phoneValid
                  ? "login-modal-sheet__phone-helper login-modal-sheet__phone-helper--valid"
                  : "login-modal-sheet__phone-helper",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <button
                      type="button"
                      className="login-modal-sheet__country"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCountryMenuAnchor(e.currentTarget);
                      }}
                    >
                      <img className="login-modal-sheet__flag" src={selectedCountry.flag} alt={selectedCountry.name} />
                      <span className="login-modal-sheet__dial">{selectedCountry.dial_code}</span>
                      <KeyboardArrowDownIcon style={{ fontSize: 20 }} />
                    </button>
                  </InputAdornment>
                ),
              }}
            />
            <Menu
              anchorEl={countryMenuAnchor}
              open={Boolean(countryMenuAnchor)}
              onClose={() => setCountryMenuAnchor(null)}
              disablePortal={false}
              sx={{ zIndex: 140050, direction: isRtl ? "rtl" : "ltr" }}
              PaperProps={{ style: { maxHeight: 300, direction: isRtl ? "rtl" : "ltr" } }}
              MenuListProps={{ autoFocusItem: false }}
            >
              {countryList.map((country) => (
                <MenuItem key={country.code} selected={selectedCountry.code === country.code} onClick={() => handleCountryChange(country.code)}>
                  <img className="login-modal-sheet__flag" src={country.flag} alt={country.name} style={{ marginInlineEnd: 8 }} />
                  <span style={{ marginInlineEnd: 8 }}>{t(`Login.country-${country.code}`)}</span>
                  <span className="text-muted">{country.dial_code}</span>
                </MenuItem>
              ))}
            </Menu>
          </div>

          <div className="login-modal-sheet__field">
            <TextField
              fullWidth
              label={t("Login.Full-name")}
              type="text"
              name="fullName"
              value={form.fullName}
              onChange={updateField}
              variant="outlined"
              autoComplete="off"
              dir={isRtl ? "rtl" : "ltr"}
              inputProps={{ style: { textAlign: isRtl ? "right" : "left" } }}
            />
          </div>
          <div className="login-modal-sheet__field">
            <TextField
              fullWidth
              label={t("Login.Email")}
              type="email"
              name="email"
              value={form.email}
              onChange={updateField}
              variant="outlined"
              autoComplete="off"
              dir="ltr"
              inputProps={{ style: { textAlign: "left" } }}
            />
          </div>
          <Button
            type="submit"
            className="login-modal-sheet__primary process-btn familysora700"
            disabled={loading || !phoneValid || !nameValid}
          >
            {loading ? t("Login.Please-wait") : otpRequired ? t("Login.Verify") : t("Login.Continue")}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default withTranslation()(LoginModalv2Popup);
